import React, { useEffect, useMemo, useState } from 'react';
import Icon from '@/components/Icon';
import { ApiError, api } from '@/lib/api';
import { useCan } from '@/auth/AuthContext';

/**
 * Модалка с расхождениями после «Пересчитать».
 *
 * Показывается, если backend вернул `changed: []` с непустым массивом.
 * Каждая строка — «было / стало / разница» + статус «есть ли документ в 1С».
 * Те строки, что выгружены в 1С, выделены — там сумма документа реализации
 * теперь расходится с CRM, и их нужно перевыгрузить кнопкой ниже.
 *
 * Кнопка «Перевыгрузить в 1С (N)» делает bulk-PUT в 1С через расширение
 * (POST /api/charges/main-monthly/onec-update). После успешного PUT в 1С
 * меняется сумма у документа реализации и счёт-фактуры.
 */

function fmtMoney(v) {
  const n = Number(v) || 0;
  return n.toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' ₽';
}
function signedMoney(v) {
  const n = Number(v) || 0;
  const sign = n > 0 ? '+' : (n < 0 ? '−' : '');
  return sign + Math.abs(n).toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' ₽';
}

export default function RecomputedChangesModal({ summary, month, onClose, onAfterPush }) {
  const can = useCan();
  const canSendOnec = can('extra.charges.onec', 'write');

  const changed = summary?.changed || [];
  const total         = summary?.total         || 0;
  const updated       = summary?.updated       || 0;
  const changedCount  = summary?.changedCount  || changed.length;
  const uploadedCount = summary?.changedUploadedCount || 0;

  // Сортировка: «расхождения в 1С» наверх — они приоритетные.
  const rows = useMemo(
    () => [...changed].sort((a, b) => (b.wasUploaded === a.wasUploaded ? 0 : (b.wasUploaded ? 1 : -1))),
    [changed]
  );

  // По умолчанию выделяем все строки, у которых документ уже в 1С —
  // это и есть «требует перевыгрузки». Остальные (не выгруженные)
  // выделять смысла нет: они пойдут в 1С обычной кнопкой «Выгрузить в 1С».
  const defaultSelected = useMemo(() => {
    const s = new Set();
    for (const r of rows) if (r.wasUploaded) s.add(r.chargeId);
    return s;
  }, [rows]);
  const [selectedIds, setSelectedIds] = useState(defaultSelected);

  // Если открыли модалку повторно (на другой результат) — пересоздаём выбор.
  useEffect(() => { setSelectedIds(defaultSelected); }, [defaultSelected]);

  const [busy, setBusy] = useState(false);
  // Локальный «лог» по строкам: 'updated' / 'error' / 'skipped' / undefined
  const [results, setResults] = useState({});

  const toggleOne = (id, on) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (on) next.add(id); else next.delete(id);
      return next;
    });
  };
  const uploadedRows = useMemo(() => rows.filter((r) => r.wasUploaded), [rows]);
  const allUploadedSelected = uploadedRows.length > 0
    && uploadedRows.every((r) => selectedIds.has(r.chargeId));
  const toggleAllUploaded = () => {
    setSelectedIds((prev) => {
      if (allUploadedSelected) {
        const next = new Set(prev);
        for (const r of uploadedRows) next.delete(r.chargeId);
        return next;
      }
      const next = new Set(prev);
      for (const r of uploadedRows) next.add(r.chargeId);
      return next;
    });
  };

  const onPush = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    if (!window.confirm(
      `Перевыгрузить выбранные начисления в 1С (${ids.length} шт.)?\n\n`
      + 'У документов реализации (и счетов-фактур) поменяются суммы. '
      + 'Если 1С недоступна — обновятся только те, что прошли; остальные '
      + 'останутся как есть, можно повторить позже.'
    )) return;

    setBusy(true);
    try {
      const res = await api.post('/api/charges/main-monthly/onec-update', { ids });
      const r = {};
      for (const it of (res.items || [])) {
        r[it.id] = { status: it.status, error: it.error || null };
      }
      setResults(r);
      // Уберём из выбора успешные — чтобы повторное нажатие не дёргало
      // их же ещё раз; ошибочные остаются выделенными для ретрая.
      setSelectedIds((prev) => {
        const next = new Set(prev);
        for (const it of (res.items || [])) {
          if (it.status === 'updated' || it.status === 'skipped') next.delete(it.id);
        }
        return next;
      });
      onAfterPush && onAfterPush(res);
    } catch (e) {
      alert(e instanceof ApiError ? e.message : 'Не удалось перевыгрузить');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="sd-overlay"
      onClick={(e) => { if (e.target.classList.contains('sd-overlay') && !busy) onClose(); }}
    >
      <div className="sd-modal sd-modal--medium" role="dialog" aria-modal="true" style={{ maxWidth: 920 }}>
        <div className="sd-edit__head">
          <h2>Пересчёт начислений · расхождения</h2>
          <button className="sd-close" onClick={onClose} disabled={busy}><Icon name="x" size={14} /></button>
        </div>

        <div className="sd-edit__body" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ fontSize: 13, color: 'var(--fg-muted)', lineHeight: 1.5 }}>
            Месяц <b>{month}</b>: всего договоров <b>{total}</b>,
            пересчитано <b>{updated}</b>,
            с изменившейся суммой <b style={{ color: 'var(--orange-700, #b45309)' }}>{changedCount}</b>
            {uploadedCount > 0 && (
              <> · из них уже выгружено в 1С <b style={{ color: 'var(--red-fg)' }}>{uploadedCount}</b></>
            )}.
            {uploadedCount > 0 && (
              <div style={{ marginTop: 6, padding: 8, background: 'var(--panel-2)', borderRadius: 6 }}>
                ⚠ У выделенных строк сумма в CRM теперь отличается от документа реализации в 1С.
                Отметьте чекбоксами те, что нужно синхронизировать, и нажмите «Перевыгрузить в 1С» —
                в 1С пройдёт PUT (через расширение <code>accrualsPUT</code>), документ реализации
                и счёт-фактура обновят сумму.
              </div>
            )}
          </div>

          {rows.length === 0 ? (
            <div className="ref-empty" style={{ padding: 16 }}>
              После пересчёта суммы не изменились.
            </div>
          ) : (
            <div className="table-wrap">
              <table className="table" style={{ fontSize: 12.5 }}>
                <thead>
                  <tr>
                    <th style={{ width: 32 }}>
                      {uploadedRows.length > 0 && (
                        <input
                          type="checkbox"
                          checked={allUploadedSelected}
                          onChange={toggleAllUploaded}
                          disabled={!canSendOnec || busy}
                          title="Выбрать/снять все строки, уже выгруженные в 1С"
                        />
                      )}
                    </th>
                    <th>Ученик</th>
                    <th style={{ width: 90 }}>Тип</th>
                    <th style={{ width: 130, textAlign: 'right' }}>Было</th>
                    <th style={{ width: 130, textAlign: 'right' }}>Стало</th>
                    <th style={{ width: 120, textAlign: 'right' }}>Разница</th>
                    <th style={{ width: 220 }}>1С</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => {
                    const checked = selectedIds.has(r.chargeId);
                    const result  = results[r.chargeId];
                    return (
                      <tr
                        key={r.chargeId}
                        style={r.wasUploaded ? { background: 'var(--panel-2)' } : undefined}
                      >
                        <td>
                          {r.wasUploaded && r.onecDocumentId ? (
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={(e) => toggleOne(r.chargeId, e.target.checked)}
                              disabled={!canSendOnec || busy}
                              title="Включить в перевыгрузку"
                            />
                          ) : null}
                        </td>
                        <td>
                          <div style={{ fontWeight: 500 }}>{r.studentName || `#${r.studentId}`}</div>
                          {r.agreementNumber && (
                            <div className="muted" style={{ fontSize: 11 }}>
                              Договор № {r.agreementNumber}
                            </div>
                          )}
                        </td>
                        <td>
                          <span className="agr-row__type-pill">
                            {r.agreementType === 'school' ? 'Школа' :
                              r.agreementType === 'kindergarten' ? 'Сад' : (r.agreementType || '—')}
                          </span>
                        </td>
                        <td className="mono" style={{ textAlign: 'right' }}>{fmtMoney(r.oldAmount)}</td>
                        <td className="mono" style={{ textAlign: 'right' }}><b>{fmtMoney(r.newAmount)}</b></td>
                        <td
                          className="mono"
                          style={{
                            textAlign: 'right',
                            color: r.diff > 0 ? 'var(--green-700)' : (r.diff < 0 ? 'var(--red-fg)' : 'var(--fg-muted)'),
                            fontWeight: 600,
                          }}
                        >
                          {signedMoney(r.diff)}
                        </td>
                        <td>
                          {r.wasUploaded && r.onecDocumentId ? (
                            <div>
                              <span
                                className={'pay-st ' + (
                                  result?.status === 'updated' ? 'pay-st--ok'
                                  : result?.status === 'error' ? 'pay-st--err'
                                  : 'pay-st--err'
                                )}
                                style={{ fontSize: 11 }}
                                title={result?.error || (result?.status === 'updated'
                                  ? 'Документ в 1С обновлён'
                                  : 'Сумма в документе 1С отличается от CRM')}
                              >
                                {result?.status === 'updated' ? '✓ синхронизировано'
                                  : result?.status === 'error' ? 'ошибка'
                                  : 'расхождение'}
                              </span>
                              <div className="mono muted" style={{ fontSize: 11, marginTop: 2 }}>
                                {r.onecDocumentId}
                              </div>
                              {result?.status === 'error' && result.error && (
                                <div style={{ fontSize: 11, color: 'var(--red-fg)', marginTop: 2 }}>
                                  {result.error}
                                </div>
                              )}
                            </div>
                          ) : (
                            <span className="muted">не выгружено</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="sd-edit__foot">
          {selectedIds.size > 0 && (
            <span style={{ fontSize: 12.5, color: 'var(--fg-muted)' }}>
              Выбрано: <b>{selectedIds.size}</b>
            </span>
          )}
          <div className="sd-edit__foot-spacer" />
          <button type="button" className="btn btn--sm" onClick={onClose} disabled={busy}>
            Закрыть
          </button>
          {canSendOnec && uploadedRows.length > 0 && (
            <button
              type="button"
              className="btn btn--sm btn--primary"
              onClick={onPush}
              disabled={busy || selectedIds.size === 0}
            >
              <Icon name="upload" size={12} />{' '}
              {busy ? 'Отправляем…' : `Перевыгрузить в 1С (${selectedIds.size})`}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
